import { FunctionComponent, useEffect, useState } from 'react'
import styles from './noDaycare.module.scss'
import { Role } from '@/types/Role'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import ButtonText from '@/components/_custom/Buttons/ButtonText/ButtonText'
import UserInput from '@/components/_custom/UserInput/userInput'
import QRCodeScannerDialog from './Dialog/QRcodeScannerDialog.tsx'
import SuggestDaycareDialog from './Dialog/suggestDaycare'
import { useLazyQuery, useMutation } from '@apollo/client'
import { USE_AUTO_LINK } from '@/modules/API/software/autoLinkHandler'
import { Browser } from '@capacitor/browser'
import { USE_STAFF_AUTO_LINK } from '@/modules/API/software/staffAutoLinkHandler'
import validator from 'validator'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { Add, CameraAlt, LinkSharp, Search } from '@mui/icons-material'
import Loading from '@/components/Loading/Loading'
import { removeServiceWorker } from '@/modules/utils/serviceWorker'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { GET_ACCOUNT, SEARCH_DAYCARE_COUNT, SET_DATA_SHARING_CONSENT } from '@/modules/API/account/accountHandler'
import EditIcon from '@mui/icons-material/Edit'
import { GET_PARENT } from '@/modules/API/parent/parentHandler'
import NoChildsDialog from '../account/childs/parentChilds/Dialog/noChilds'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import AnnuaireTrackingHandler from '@/components/AnnuaireTracking/AnnuaireTrackingHandler'

interface NoDaycareProps {
    role: Role
}

const NoDaycare: FunctionComponent<NoDaycareProps> = ({ role }: NoDaycareProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const navigate: NavigateFunction = useNavigate()

    const [joinDaycare, setJoinDaycare] = useState<boolean>(false)

    const [profilNotCompleted, setProfilNotCompleted] = useState<boolean>(false)

    const [isDialogQRCodeOpen, setDialogQRCodeOpen] = useState<boolean>(false)
    const [isDialogSuggestOpen, setDialogSuggestOpen] = useState<boolean>(false)

    const [autoLinkURL, setAutoLinkURL] = useState<string>('')

    const [parentHaveChilds, setParentHaveChilds] = useState<boolean>(true)

    const [isDialogNoChildsOpen, setDialogNoChildsOpen] = useState<boolean>(false)

    const [dataSharingConsent, setDataSharingConsent] = useState<boolean>(false)

    const [getParentData, { loading: loadingParent, data: dataParent, error: errorParent }] = useLazyQuery(GET_PARENT)
    const [getAccountData, { loading: loadingAccount, data: dataAccount }] = useLazyQuery(GET_ACCOUNT)

    const [setDataSharingConsentMutation] = useMutation(SET_DATA_SHARING_CONSENT)

    useEffect(() => {
        if (dataParent) {
            setParentHaveChilds(dataParent.getParent.childs ? dataParent.getParent.childs.length > 0 : false)
            setDataSharingConsent(dataParent.getParent.dataSharingConsent || false)
        }
    }, [dataParent])

    useEffect(() => {
        if (dataAccount) {
            setDataSharingConsent(dataAccount.getAccount.dataSharingConsent || false)
        }
    }, [dataAccount])

    useEffect(() => {
        if (errorParent) {
            setParentHaveChilds(false)
        }
    }, [errorParent])

    function handleDataSharingConsentChange(value: boolean): void {
        setDataSharingConsent(value)
        setDataSharingConsentMutation({ variables: { consent: value } })
    }

    useEffect(() => {
        if (role === Role.parent) getParentData()
        else if (role === Role.staff) getAccountData()
    }, [role])

    function handleConfirmAutoLink(): void {
        try {
            const url: URL = new URL(autoLinkURL)
            const searchParams: URLSearchParams = new URLSearchParams(url.search)
            const autoLinkValue: string | null = searchParams.get('autoLink')
            if (autoLinkValue) {
                if (role === Role.parent) {
                    autoLinkMutation({ variables: { autoLink: autoLinkValue } })
                } else {
                    autoLinkStaffMutation({ variables: { autoLink: autoLinkValue } })
                }
            } else {
                showNotificationSuccess(t('autoLinkInvalid'))
            }
        } catch (error) {
            showNotificationError(t('autoLinkInvalid'))
        }
    }

    const [autoLinkMutation, { loading: loadingAutoLink }] = useMutation(USE_AUTO_LINK, {
        onCompleted: async (dataAutoLink) => {
            removeServiceWorker()
            await Browser.open({
                url: '/daycare?daycare=' + dataAutoLink.useAutoLink,
                windowName: '_self'
            })
        },
        onError: () => {
            showNotificationError(t('autoLinkInvalid'))
        }
    })

    const [autoLinkStaffMutation, { loading: loadingAutoLinkStaff }] = useMutation(USE_STAFF_AUTO_LINK, {
        onCompleted: async (dataAutoLink) => {
            removeServiceWorker()
            await Browser.open({
                url: '/daycare?daycare=' + dataAutoLink.useStaffAutoLink,
                windowName: '_self'
            })
        },
        onError: () => {
            showNotificationError(t('autoLinkInvalid'))
        }
    })

    function searchDaycareCount(): void {
        searchDaycareMutation()
    }

    const [
        searchDaycareMutation,
        { loading: loadingSearchDaycare, data: dataSearchDaycare, error: errorSearchDaycare }
    ] = useLazyQuery(SEARCH_DAYCARE_COUNT)

    useEffect(() => {
        if (dataSearchDaycare) {
            const annuaireUrl: string = import.meta.env.VITE_ANNUAIRE_URL as string
            const country: string | undefined = dataParent?.getParent?.country ?? dataAccount?.getAccount?.country
            const redirectUrl: string = country ? `${annuaireUrl}/country/${country}` : annuaireUrl
            Browser.open({ url: redirectUrl, windowName: '_blank' })
        }
    }, [dataSearchDaycare])

    useEffect(() => {
        if (errorSearchDaycare && errorSearchDaycare.message.split('Message: ')[0] === 'User could not be geocoded.') {
            setProfilNotCompleted(true)
        }
    }, [errorSearchDaycare])

    useEffect(() => {
        if (!parentHaveChilds) {
            setDialogNoChildsOpen(true)
        }
    }, [parentHaveChilds])

    return (
        <div className={styles.PagePosition}>
            <AnnuaireTrackingHandler role={role} onConsentAccepted={() => setDataSharingConsent(true)} />
            {(loadingAutoLink || loadingAutoLinkStaff || loadingParent || loadingAccount) && <Loading overall={true} />}
            <div className={styles.NoDaycareAlert}>
                {!joinDaycare ? (
                    <>
                        <Title text={t('noDaycareTitle')} className={styles.Title} />
                        {role === Role.staff && (
                            <ButtonTextIcon
                                customText={t('iWantToCreateNewDaycare')}
                                onClick={() => navigate('/createDaycare')}
                                className={styles.Button}
                                customIcon={<Add />}
                                type={'custom'}
                            />
                        )}
                        <ButtonTextIcon
                            customText={t('iWantToJoinMyDaycare')}
                            onClick={() => setJoinDaycare(true)}
                            parent={role === Role.parent}
                            className={styles.Button}
                            customIcon={<LinkSharp />}
                            type={'custom'}
                        />
                        {!dataSharingConsent && (
                            <div className={styles.ConsentCheckbox}>
                                <Checkbox
                                    text={t('dataSharingConsent')}
                                    checked={dataSharingConsent}
                                    onChange={handleDataSharingConsentChange}
                                    parent={role === Role.parent}
                                />
                            </div>
                        )}
                        <ButtonTextIcon
                            customText={t('iSearchADaycare')}
                            onClick={() => searchDaycareCount()}
                            parent={role === Role.parent}
                            className={styles.Button}
                            customIcon={<Search />}
                            type={'custom'}
                            disabled={loadingSearchDaycare || !dataSharingConsent}
                        />
                        {profilNotCompleted && (
                            <>
                                <p className={`${role === Role.parent ? styles.LineParent : styles.LineDaycare}`}></p>
                                <p className={styles.Input}>{t('yourProfileWasNotCompleted')}</p>
                                <ButtonTextIcon
                                    type={'custom'}
                                    customIcon={<EditIcon />}
                                    customText={t('editMyProfile')}
                                    parent={role === Role.parent}
                                    onClick={() => navigate('/account/profile')}
                                />
                            </>
                        )}
                    </>
                ) : joinDaycare ? (
                    <>
                        <Title text={t('joinDaycare')} className={styles.Title} />
                        <UserInput
                            className={styles.Input}
                            label={t('autoLink')}
                            value={autoLinkURL}
                            setValue={setAutoLinkURL}
                            verify={'autoLink'}
                            required
                            parent={role === Role.parent}
                            fullWidth
                        />
                        <ButtonTextIcon
                            type={'custom'}
                            customIcon={<CameraAlt />}
                            customText={t('scanWithQRCode')}
                            onClick={() => setDialogQRCodeOpen(true)}
                            parent={role === Role.parent}
                            fullWidth
                            className={styles.Input}
                        />
                        <ButtonTextIcon
                            type={'confirm'}
                            onClick={handleConfirmAutoLink}
                            parent={role === Role.parent}
                            fullWidth
                            disabled={!validator.isURL(autoLinkURL)}
                            className={styles.LastButton}
                        />
                        <p className={`${role === Role.parent ? styles.LineParent : styles.LineDaycare}`}></p>
                        <p className={styles.Input}> {t('suggestDaycareText')} </p>
                        <ButtonText
                            text={t('suggestDaycare')}
                            onClick={() => setDialogSuggestOpen(true)}
                            parent={role === Role.parent}
                            fullWidth
                        />
                        <a
                            className={role === Role.parent ? styles.LinkParent : styles.LinkDaycare}
                            onClick={() => setJoinDaycare(false)}
                        >
                            {t('return')}
                        </a>
                    </>
                ) : null}
            </div>
            <SuggestDaycareDialog
                open={isDialogSuggestOpen}
                handleClose={() => setDialogSuggestOpen(false)}
                parent={role === Role.parent}
            />
            <QRCodeScannerDialog
                open={isDialogQRCodeOpen}
                handleClose={() => setDialogQRCodeOpen(false)}
                parent={role === Role.parent}
                setAutoLinkURL={setAutoLinkURL}
            />
            {isDialogNoChildsOpen && role === Role.parent && (
                <NoChildsDialog
                    open={isDialogNoChildsOpen}
                    handleClose={() => setDialogNoChildsOpen(false)}
                    addChild={() => navigate('/account/childs')}
                />
            )}
        </div>
    )
}

export default NoDaycare
